import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { PackageCheck, Star } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardBody } from '@/components/ui/card'
import { Dialog } from '@/components/ui/dialog'
import { EmptyState } from '@/components/ui/empty-state'
import { Textarea } from '@/components/ui/field'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs } from '@/components/ui/tabs'
import { ContractStatusBadge, tierLabel } from '@/components/ui/status'
import { useToast } from '@/components/ui/toast'
import { useReviewServiceOrder, useServiceOrders } from '@/features/services/api'
import { ApiError } from '@/lib/api'
import { formatDate, formatRupiah } from '@/lib/format'
import type { ServiceOrder } from '@/lib/types'

export function ServiceOrdersPage() {
  const [role, setRole] = useState<'buyer' | 'seller'>('buyer')
  const { data, isLoading } = useServiceOrders(role)
  const [reviewTarget, setReviewTarget] = useState<ServiceOrder | null>(null)

  const orders = data?.orders ?? []

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-extrabold tracking-tight text-ink sm:text-3xl">Pesanan jasa</h1>
        <p className="mt-1 text-muted">
          Setiap pesanan berjalan di atas kontrak escrow; pengerjaannya dipantau dari halaman kontrak.
        </p>
      </header>

      <Tabs
        value={role}
        onChange={setRole}
        items={[
          { value: 'buyer', label: 'Saya memesan' },
          { value: 'seller', label: 'Saya mengerjakan' },
        ]}
      />

      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, index) => (
            <Skeleton key={index} className="h-28 rounded-2xl" />
          ))}
        </div>
      ) : orders.length === 0 ? (
        <Card>
          <EmptyState illustration="box"
            icon={<PackageCheck className="size-6" />}
            title={role === 'buyer' ? 'Belum ada pesanan' : 'Belum ada pesanan masuk'}
            description={
              role === 'buyer'
                ? 'Jasa yang Anda pesan akan muncul di sini beserta status kontraknya.'
                : 'Pesanan dari pembeli akan muncul di sini begitu masuk.'
            }
            action={
              role === 'buyer' ? (
                <Link to="/jasa">
                  <Button>Jelajahi paket jasa</Button>
                </Link>
              ) : (
                <Link to="/app/jasa">
                  <Button variant="outline">Kelola etalase jasa</Button>
                </Link>
              )
            }
          />
        </Card>
      ) : (
        <div className="space-y-3">
          {orders.map((order) => (
            <Card key={order.id}>
              <CardBody className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <ContractStatusBadge state={order.contract_state} />
                    <span className="text-xs text-subtle">
                      Paket {tierLabel[order.package_tier] ?? order.package_tier} ·{' '}
                      {formatDate(order.created_at)}
                    </span>
                  </div>
                  <h2 className="mt-2 font-bold text-ink">{order.listing.title}</h2>
                  <p className="mt-0.5 text-sm text-muted">
                    {role === 'buyer' ? `Dikerjakan ${order.seller.full_name}` : `Dipesan ${order.buyer.full_name}`}
                  </p>
                  <p className="clamp-2 mt-2 text-sm text-muted">{order.requirements}</p>
                </div>

                <div className="shrink-0 text-right">
                  <p className="text-lg font-extrabold tabular-nums text-ink">{formatRupiah(order.amount)}</p>
                  <div className="mt-3 flex flex-wrap justify-end gap-2">
                    <Link to="/app/kontrak/$contractId" params={{ contractId: order.contract_id }}>
                      <Button size="sm">Buka kontrak</Button>
                    </Link>
                    {role === 'buyer' && order.contract_state === 'completed' && !order.reviewed ? (
                      <Button size="sm" variant="outline" onClick={() => setReviewTarget(order)}>
                        <Star className="size-4" />
                        Beri ulasan
                      </Button>
                    ) : null}
                  </div>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}

      <ReviewDialog order={reviewTarget} onClose={() => setReviewTarget(null)} />
    </div>
  )
}

function ReviewDialog({ order, onClose }: { order: ServiceOrder | null; onClose: () => void }) {
  const review = useReviewServiceOrder()
  const toast = useToast()
  const [rating, setRating] = useState(5)
  const [comment, setComment] = useState('')

  return (
    <Dialog
      open={order !== null}
      onClose={onClose}
      size="sm"
      title="Beri ulasan"
      description={order?.listing.title}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Batal
          </Button>
          <Button
            loading={review.isPending}
            onClick={() => {
              if (!order) return
              review.mutate(
                { orderId: order.id, rating, comment },
                {
                  onSuccess: () => {
                    toast.success('Ulasan tersimpan', 'Terima kasih sudah membantu pembeli lain.')
                    setComment('')
                    onClose()
                  },
                  onError: (error) =>
                    toast.error('Gagal menyimpan ulasan', error instanceof ApiError ? error.message : undefined),
                },
              )
            }}
          >
            Kirim ulasan
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <p className="text-sm font-semibold text-ink">Seberapa puas Anda?</p>
          <div className="mt-2 flex gap-1">
            {[1, 2, 3, 4, 5].map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => setRating(value)}
                aria-label={`Beri ${value} bintang`}
                className="rounded-md p-1 transition hover:scale-110"
              >
                <Star className={`size-6 ${value <= rating ? 'fill-amber-400 text-amber-400' : 'text-subtle'}`} />
              </button>
            ))}
          </div>
        </div>

        <Textarea
          label="Catatan"
          rows={4}
          value={comment}
          onChange={(event) => setComment(event.target.value)}
          placeholder="Bagian mana yang paling membantu, dan apa yang masih bisa diperbaiki?"
        />
      </div>
    </Dialog>
  )
}
